/**
 * Ce qu'une facture tirée d'un devis réclamera, calculé **avant** de la créer.
 *
 * Pourquoi le noyau recalcule ce que la fonction Edge calculera : le résumé
 * qu'on fait confirmer au patron doit dire un montant, et ce montant doit être
 * celui qui partira. Deux calculs, donc — mais **le second vérifie le premier** :
 * `facturer_et_envoyer` compare la facture créée au montant annoncé, et refuse
 * de l'envoyer s'ils diffèrent. Un écart ne produit jamais un envoi faux, au
 * pire une facture en brouillon et un message qui dit pourquoi.
 *
 * Les deux règles recopient, ligne pour ligne, ce qu'elles vérifient :
 *
 *   · la situation — `computeTotals` dans `create-invoice-from-quote` : chaque
 *     total du devis multiplié par le pourcentage, arrondi au centime ;
 *   · le solde — `quote_prior_invoiced` en base : acomptes et situations du
 *     devis, hors brouillons et annulées, déduits du total.
 */
import type { InvoiceStatus, InvoiceType } from "../invoices/types.js";
import { round2 } from "../../money.js";

export interface QuoteTotalsRef {
  totalHt: number;
  totalTtc: number;
}

export interface BillingAmounts {
  /** Ce que la facture réclame, hors taxes. */
  amountHt: number;
  amountTtc: number;
}

/**
 * Le pourcentage d'une situation, arrondi comme la fonction l'arrondira.
 *
 * Deux décimales : assez pour qu'un écart de 17,34 points ne devienne pas 17,
 * et c'est aussi la précision de la colonne `situation_percent`.
 */
export function situationPercent(points: number): number {
  return round2(Math.min(100, Math.max(0, points)));
}

export function situationAmounts(quote: QuoteTotalsRef, percent: number): BillingAmounts {
  const p = percent / 100;
  return { amountHt: round2(quote.totalHt * p), amountTtc: round2(quote.totalTtc * p) };
}

/** Une facture déjà émise d'un devis, telle que la déduction la lit. */
export interface PriorInvoiceRef {
  type: InvoiceType;
  status: InvoiceStatus;
  totalHt: number;
  totalTtc: number;
}

/**
 * Ce qui pèse sur le solde, et ce qui n'y pèse pas.
 *
 * **Les types.** Un acompte et une situation réclament une part du marché,
 * donc ils se déduisent. Un avoir aussi, et c'est le choix de fond : il
 * **corrige** ce qu'un partiel a réclamé de trop. Négatif par construction,
 * il s'additionne aux autres et ramène la déduction à ce qui reste dû. Une
 * situation de 12 000 corrigée par un avoir de 2 000 a réclamé 10 000, et
 * c'est 10 000 que le solde doit déduire — pas 12 000, ce qui perdrait la
 * correction, ni 0, ce qui la ferait payer deux fois.
 *
 * La facture de solde elle-même ne s'y trouve pas : elle **est** le calcul.
 *
 * **Les statuts.** Ce qui n'a jamais été réclamé ne se déduit pas : un
 * brouillon n'est pas parti chez le client, une facture annulée ne vaut
 * plus rien.
 *
 * `disputed` n'en fait PAS partie, et la nuance coûte cher : une facture
 * contestée a bel et bien été réclamée. La sortir de la déduction la ferait
 * réclamer une seconde fois par le solde.
 *
 * `credit_note_issued` non plus, et c'est délibéré : un partiel avoiré est
 * déjà neutralisé par l'avoir, qui est compté juste au-dessus. L'exclure EN
 * PLUS le retirerait deux fois, et le solde réclamerait trop.
 *
 * Même frontière que `quote_prior_invoiced`, au mot près : les deux doivent
 * bouger ensemble, sinon `facturer_et_envoyer` refuse d'envoyer sur l'écart
 * qu'il constate.
 */
const PARTIELS = new Set<InvoiceType>(["deposit", "situation", "credit_note"]);
const NON_RECLAMEES = new Set<InvoiceStatus>(["draft", "cancelled"]);

export function priorInvoiced(invoices: readonly PriorInvoiceRef[]): BillingAmounts {
  const partiels = invoices.filter(
    (f) => PARTIELS.has(f.type) && !NON_RECLAMEES.has(f.status),
  );
  return {
    amountHt: round2(partiels.reduce((s, f) => s + f.totalHt, 0)),
    amountTtc: round2(partiels.reduce((s, f) => s + f.totalTtc, 0)),
  };
}

/**
 * Le solde : le devis, moins ce qui reste réclamé par les partiels — avoirs
 * déduits, puisqu'ils les corrigent.
 */
export function finalInvoiceAmounts(
  quote: QuoteTotalsRef,
  prior: BillingAmounts,
): BillingAmounts {
  return {
    amountHt: round2(quote.totalHt - prior.amountHt),
    amountTtc: round2(quote.totalTtc - prior.amountTtc),
  };
}
